import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Flex,
    IconButton,
    IconControlledSubstance,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../../graphql';
import { useFlags } from '../../../hooks/use-flags';
import { getAbsoluteRoute } from '../../../util';
import { RxForUser } from '../../cabinet/rx-for-user/RxForUser';
import { DrugCoverageAlerts } from '../../common/drug-coverage-alerts/DrugCoverageAlerts';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import type { PatientProductDetailsProps } from './PatientProductDetails';

export const PatientProductDetailsHeader = ({
    patientName,
    productDescription,
    quantity,
    prescriberOrder,
    coverageFlags,
    togglePARequiredModal,
    toggleNotCoveredModal,
}: PatientProductDetailsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { helpCenterMedicationEnabled } = useFlags();

    const controlledSubstanceCode = getFragment(
        prescriberOrder?.authorizedMedication
    )?.controlledSubstanceCode;

    return (
        <Stack gap="xxs">
            <ConditionalComponent condition={!!patientName}>
                {prescriberOrder ? (
                    <RxForUser patient={{ fullName: patientName }} />
                ) : (
                    <Subtitle2>
                        {t('priorAuth.paForUser.header', { patientName })}
                    </Subtitle2>
                )}
            </ConditionalComponent>
            <Flex gap="xxs">
                <Subtitle1 data-testid="prescriber-order-product-details-header">
                    {productDescription}
                </Subtitle1>
                {controlledSubstanceCode ? (
                    <IconButton
                        Icon={IconControlledSubstance}
                        withStyles={false}
                        onClick={
                            helpCenterMedicationEnabled
                                ? () =>
                                      navigate(
                                          getAbsoluteRoute(
                                              'helpCenter.medications'
                                          )
                                      )
                                : undefined
                        }
                    />
                ) : null}
            </Flex>
            <ConditionalComponent condition={!!quantity}>
                <Body1>
                    {t('orderDetails.quantity', {
                        quantity,
                    })}
                </Body1>
            </ConditionalComponent>
            {coverageFlags && (
                <DrugCoverageAlerts
                    coverageFlags={coverageFlags}
                    togglePARequiredModal={togglePARequiredModal}
                    toggleNotCoveredModal={toggleNotCoveredModal}
                />
            )}
        </Stack>
    );
};
